import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError, createEmailTemplate, listEmailTemplates, type EmailTemplateCreate } from "@/api/client";
import { Field, OtherProblems, inputClass, serverProblems, submitClass, useRevealProblems } from "@/components/form";
import { formatDate } from "@/components/format";
import { MERGE_FIELDS } from "@/features/email/choices";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader, secondaryButtonClass } from "@/components/ui";
import { ScrollArea } from "@/components/ScrollArea";

const templatesApi = getRouteApi("/staff/email-templates");

export function EmailTemplateList() {
  const archived = templatesApi.useSearch().archived === 1;
  const navigate = useNavigate();
  const templates = useQuery({
    queryKey: ["email-templates", { archived }],
    queryFn: () => listEmailTemplates({ includeArchived: archived }),
  });
  const items = templates.data ?? [];
  const [creating, setCreating] = useState(false);

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Email templates"
        detail="The wording of certificate emails. Each program can choose one; others use the default."
        action={
          <ToolbarButton disabled={creating} onClick={() => setCreating(true)}>
            New template
          </ToolbarButton>
        }
      />
      <ErrorBanner error={templates.error} />
      {creating ? <NewTemplate onCancel={() => setCreating(false)} /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Templates</h2>
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => void navigate({ to: "/email-templates", search: { archived: e.target.checked ? 1 : undefined } })}
            />
            Show archived
          </label>
        </div>
        {templates.isLoading ? (
          <EmptyState title="Loading email templates" detail="Fetching templates." />
        ) : templates.isSuccess && items.length === 0 ? (
          <EmptyState title="No email templates yet" detail="Add one to set the wording of certificate emails." />
        ) : (
          <ScrollArea label="Email templates">
            <table aria-label="Email templates" className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">Programs</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((template) => (
                  <tr key={template.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/email-templates/$templateId"
                        params={{ templateId: template.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {template.name}
                      </Link>
                      {template.is_default ? (
                        <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Default</span>
                      ) : null}
                      {template.archived_at ? (
                        <span className="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">Archived</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{template.subject}</td>
                    <td className="px-4 py-3 text-zinc-700">{template.program_count}</td>
                    <td className="px-4 py-3 text-zinc-500">{formatDate(template.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </section>
  );
}

type TemplateField = "name" | "subject" | "body_text";
const FIELDS = ["name", "subject", "body_text"] as const;

function NewTemplate({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", subject: "", body_text: "" });
  const formRef = useRef<HTMLFormElement>(null);
  const reveal = useRevealProblems<TemplateField>(formRef);
  const create = useMutation({
    mutationFn: (body: EmailTemplateCreate) => createEmailTemplate(body),
    onSuccess: async (template) => {
      queryClient.setQueryData(["email-template", template.id], template);
      await queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      await navigate({ to: "/email-templates/$templateId", params: { templateId: template.id } });
    },
  });

  const clientErrors: Partial<Record<TemplateField, string>> = {};
  for (const field of FIELDS) if (values[field].trim() === "") clientErrors[field] = "is required";
  const server = serverProblems(create.error, FIELDS);
  const conflict = create.error instanceof ApiError && create.error.status === 409 ? create.error.message : undefined;
  const problem = (field: TemplateField) =>
    reveal.shown(field, clientErrors[field]) ?? server.byField[field] ?? (field === "name" ? conflict : undefined);
  const set = (field: TemplateField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (create.error) create.reset();
  };

  return (
    <section aria-labelledby="new-email-template" className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="new-email-template" className="text-sm font-semibold">
        New email template
      </h2>
      <form
        ref={formRef}
        aria-label="New email template"
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (Object.keys(clientErrors).length > 0) {
            reveal.submitted();
            return;
          }
          if (!create.isPending) create.mutate({ name: values.name.trim(), subject: values.subject.trim(), body_text: values.body_text });
        }}
      >
        <Field label="Name" problem={problem("name")}>
          {(props) => (
            <input
              {...props}
              autoFocus
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              onBlur={() => reveal.touch("name")}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Subject" problem={problem("subject")}>
          {(props) => (
            <input
              {...props}
              value={values.subject}
              onChange={(e) => set("subject", e.target.value)}
              onBlur={() => reveal.touch("subject")}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Body" problem={problem("body_text")} hint={`Merge fields: ${MERGE_FIELDS.join(" ")}`}>
          {(props) => (
            <textarea
              {...props}
              rows={10}
              value={values.body_text}
              onChange={(e) => set("body_text", e.target.value)}
              onBlur={() => reveal.touch("body_text")}
              className={inputClass}
            />
          )}
        </Field>
        <ErrorBanner error={create.error instanceof ApiError && [409, 422].includes(create.error.status) ? null : create.error} />
        <OtherProblems lines={server.other} />
        <div className="flex gap-2">
          <button type="submit" disabled={create.isPending} className={submitClass}>
            {create.isPending ? "Creating..." : "Create template"}
          </button>
          <button type="button" onClick={onCancel} className={`rounded-md border px-3 py-2 text-sm font-medium ${secondaryButtonClass}`}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
